import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('maris_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - improved to not immediately redirect
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Check if this is NOT the refresh or login endpoint to avoid loops
      const isAuthEndpoint = error.config?.url?.includes('/auth/login') || 
                            error.config?.url?.includes('/auth/refresh') ||
                            error.config?.url?.includes('/auth/register');
      
      if (!isAuthEndpoint) {
        // Clear stored auth data
        localStorage.removeItem('maris_token');
        localStorage.removeItem('maris_user');
        // Redirect to login with a session expired flag
        const currentPath = window.location.pathname;
        if (currentPath !== '/login') {
          window.location.href = `/login?session=expired&redirect=${encodeURIComponent(currentPath)}`;
        }
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const login = (email, password) => api.post('/auth/login', { email, password });
export const register = (name, email, password, role = 'OPERATOR') => 
  api.post('/auth/register', { name, email, password, role });
export const getMe = () => api.get('/auth/me');
export const refreshToken = () => api.post('/auth/refresh');
export const forgotPassword = (email) => api.post('/auth/forgot-password', { email });
export const resetPassword = (token, new_password) => api.post('/auth/reset-password', { token, new_password });

// Users
export const getUsers = () => api.get('/users');
export const assignProperties = (userId, propertyIds) => 
  api.put(`/users/${userId}/assign-properties`, propertyIds);
export const disableUser = (userId) => api.post(`/users/${userId}/disable`);
export const enableUser = (userId) => api.post(`/users/${userId}/enable`);
export const deleteUser = (userId) => api.delete(`/users/${userId}`);
export const inviteUser = (payload) => api.post('/users/invite', payload);
export const markWelcomeSeen = () => api.put('/users/me/welcome-seen');

// Properties
export const getProperties = () => api.get('/properties');
export const getProperty = (id) => api.get(`/properties/${id}`);
export const createProperty = (data) => api.post('/properties', data);
export const updateProperty = (id, data) => api.put(`/properties/${id}`, data);
export const getPropertyBySlug = (slug) => api.get(`/properties/slug/${slug}`);

// Office Hours
export const getOfficeHours = (propertyId) => api.get(`/office-hours/${propertyId}`);
export const saveOfficeHours = (payload) => api.post('/office-hours', payload);

// Content Archive (admin-only)
export const listContentArchive = () => api.get('/admin/content-archive');
export const getContentArchiveSnapshot = (id) => api.get(`/admin/content-archive/${id}`);

// Demo reset URL (admin-only) — returns a one-time password-reset link
// for the sandboxed demo owner account so the calling admin can set a
// fresh demo password without involving email delivery.
export const mintDemoResetLink = () => api.post('/admin/mint-demo-reset');

// Guests
export const getGuests = (propertyId) => api.get('/guests', { params: { propertyId } });
export const getGuest = (id) => api.get(`/guests/${id}`);
export const createGuest = (data) => api.post('/guests', data);
export const updateGuest = (id, data) => api.put(`/guests/${id}`, data);

// Conversations
export const getConversations = (params) => api.get('/conversations', { params });
export const getConversation = (id) => api.get(`/conversations/${id}`);
export const createConversation = (data) => api.post('/conversations', data);
export const updateConversationStatus = (id, status, lostReason) => 
  api.put(`/conversations/${id}/status`, null, { params: { status, lostReason } });
export const addConversationToDailyRun = (id, data) =>
  api.post(`/conversations/${id}/add-to-daily-run`, data || {});
export const archiveConversation = (id) =>
  api.post(`/conversations/${id}/archive`);
export const setTaskNextTouch = (id, data) =>
  api.put(`/tasks/${id}/next-touch`, data);
export const setTaskAssignment = (id, data) =>
  api.put(`/tasks/${id}/assignment`, data);

// Messages
export const getMessages = (conversationId) => api.get('/messages', { params: { conversationId } });
export const createMessage = (data) => api.post('/messages', data);
export const updateNeedsReply = (id, needsReply) => 
  api.put(`/messages/${id}/needs-reply`, null, { params: { needsReply } });

// Follow-ups
export const getFollowups = (status) => api.get('/followups', { params: { status } });
export const createFollowup = (data) => api.post('/followups', data);
export const updateFollowupStatus = (id, status) => 
  api.put(`/followups/${id}/status`, null, { params: { status } });

// Reviews
export const getReviews = (params) => api.get('/reviews', { params });
export const createReview = (data) => api.post('/reviews', data);
export const updateReviewResponse = (id, draftedResponse, approved) => 
  api.put(`/reviews/${id}/response`, null, { params: { draftedResponse, approved } });
export const getReviewInsights = (propertyId) => api.get(`/reviews/insights/${propertyId}`);

// Requests (Guest Hub)
export const getRequests = (propertyId) => api.get('/requests', { params: { propertyId } });
export const createRequest = (data) => api.post('/requests', data);
export const updateRequestStatus = (id, status) => 
  api.put(`/requests/${id}/status`, null, { params: { status } });

// Tasks
export const getTasks = (params) => api.get('/tasks', { params });
export const createTask = (data) => api.post('/tasks', data);
export const updateTaskStatus = (id, status, waiting_on) => 
  api.put(`/tasks/${id}/status`, null, { params: { status, waiting_on } });
export const deleteTask = (id) => api.delete(`/tasks/${id}`);
export const updateTaskEP = (id, data) => api.put(`/tasks/${id}/experience-protection`, data);
export const getEPSummary = (propertyId) => api.get('/experience-protection/summary', { params: { propertyId } });
export const backfillEP = () => api.post('/tasks/backfill-ep');

// Shifts
export const startShift = () => api.post('/shifts/start');
export const endShift = (data) => api.post('/shifts/end', data);
export const getActiveShift = () => api.get('/shifts/active');

// Briefing (single payload)
export const getBriefingPayload = (propertyId, period) => api.get('/briefing/payload', { params: { propertyId, period } });

// Financial Impact (same source the Today widget uses — defaults to this_week)
export const getFinancialImpact = (propertyId, period = 'this_week') =>
  api.get('/dashboard/financial-impact', { params: { propertyId, period } });

// Finalized briefing reports (audit trail; created on End Shift)
export const listBriefingReports = () => api.get('/briefing-reports');
export const getBriefingReport = (reportId) => api.get(`/briefing-reports/${reportId}`);
export const getTodaysReportStatus = () => api.get('/briefing-reports/today/status');

// Demo (curated story for prospect walkthroughs)
export const resetDemo = () => api.post('/demo/reset');
export const getDemoStatus = () => api.get('/demo/status');
export const seedShowcaseData = () => api.post('/admin/seed-showcase');
export const purgeShowcaseFromLive = () => api.post('/admin/purge-showcase-from-live');
export const getLiveTodayDiagnostic = () => api.get('/admin/diagnostic/live-today-records');
export const getLiveOtherTabsDiagnostic = () => api.get('/admin/diagnostic/live-other-tabs');
export const sandboxRecordIds = (payload) => api.post('/admin/sandbox-record-ids', payload);

// Templates
export const getTemplates = (propertyId) => api.get('/templates', { params: { propertyId } });
export const createTemplate = (data) => api.post('/templates', data);

// AI
export const generateDraft = (data) => api.post('/ai/draft', data);
export const generateReviewDraft = (propertyId, reviewText, rating) => 
  api.post('/ai/review-draft', null, { params: { propertyId, reviewText, rating } });

// Dashboard
export const getTodayData = () => api.get('/dashboard/today');

// Reports
export const getPropertyReport = (propertyId, month) => 
  api.get(`/reports/${propertyId}`, { params: { month } });

// Seed
export const seedData = () => api.post('/seed');

// Escalations
export const createEscalation = (data) => api.post('/escalations', data);
export const getEscalations = (conversationId) => api.get('/escalations', { params: { conversationId } });
export const updateEscalationStatus = (id, status) => 
  api.put(`/escalations/${id}/status`, null, { params: { status } });

// Tour
export const markTourSeen = () => api.put('/users/me/tour-seen');
export const resetTour = () => api.put('/users/me/tour-reset');

// Email Briefing
export const getEmailConfig = () => api.get('/email-config');
export const updateEmailConfig = (data) => api.put('/email-config', data);
export const sendBriefing = (data) => api.post('/send-briefing', data);
export const getEmailLogs = () => api.get('/email-logs');
export const getEmailPreview = (logId) => api.get(`/email-preview/${logId}`);

// Health
export const healthCheck = () => api.get('/health');

export default api;
